import { html, render, nothing, type TemplateResult } from 'lit-html';
import listStyles from './pk-report-top-list.css?inline';
import sharedTokens from '../pk-report-shared/pk-report-tokens.css?inline';

/**
 * `<pk-report-top-list>` — ranked horizontal-bar list ("top N by metric"). v0.7
 *
 * Each row shows: label + proportional-width bar + value. Clicking a row
 * fires `pk-row-action` with `{ dimensionValue, label, value }` so the consumer
 * can narrow its filter to that dimension.
 *
 * Attributes:
 *   `title`      — optional heading text.
 *   `max-rows`   — max child rows to render (default 10). Extras are hidden.
 *   `empty-state`— (slot) alternative content when no rows are present.
 *
 * Events:
 *   `pk-row-action` — `detail: { dimensionValue: string, label: string, value: string }`.
 *
 * ```html
 * <pk-report-top-list title="Top users by cost" max-rows="10">
 *   <pk-report-top-row label="alice@fyin.com" value="$87.20" weight="1.0"></pk-report-top-row>
 *   <pk-report-top-row label="bob@fyin.com"   value="$42.10" weight="0.48"></pk-report-top-row>
 * </pk-report-top-list>
 * ```
 */
/**
 * Compat data shape for the `items` / `rows` properties (CMS-driven imperative API).
 */
export interface PkTopRowItem {
    label?:          string;
    value?:          string | number;
    weight?:         number;
    danger?:         boolean;
    dimensionValue?: string;
    dimension?:      string;
}

export class PkReportTopList extends HTMLElement {
    static readonly tagName = 'pk-report-top-list';

    static get observedAttributes(): string[] {
        return ['title', 'max-rows'];
    }

    private readonly _root: ShadowRoot;
    private readonly _slotChangeHandler: () => void;
    private _items: PkTopRowItem[] = [];

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
        this._slotChangeHandler = () => this._onSlotChange();
    }

    connectedCallback(): void {
        this._upgradeProperty('items');
        this._upgradeProperty('rows');
        this._doRender();
        // Watch for slot changes so max-rows re-applies when children arrive
        const slot = this._root.querySelector('slot:not([name])');
        slot?.addEventListener('slotchange', this._slotChangeHandler);
    }

    // ── Compat data API ────────────────────────────────────────────────────────

    /**
     * Imperative data setter (v0.8 compat). Builds `<pk-report-top-row>` children
     * from a plain array. `rows` is an alias for `items`. Backward-compatible with
     * hand-authored `<pk-report-top-row>` markup.
     */
    set items(value: PkTopRowItem[] | null | undefined) {
        this._items = Array.isArray(value) ? value : [];
        this._rebuildRows();
    }

    get items(): PkTopRowItem[] {
        return this._items;
    }

    set rows(value: PkTopRowItem[] | null | undefined) {
        this.items = value;
    }

    get rows(): PkTopRowItem[] {
        return this._items;
    }

    private _rebuildRows(): void {
        this.querySelectorAll('pk-report-top-row').forEach(el => el.remove());

        // Pre-compute magnitudes for relative-weight fallback.
        const magnitudes = this._items.map(item => {
            const m = parseFloat(String(item.value ?? '').replace(/[^0-9.\-]/g, ''));
            return isNaN(m) ? 0 : m;
        });
        const maxMag = Math.max(0, ...magnitudes);

        this._items.forEach((item, i) => {
            let weight: number;
            if (typeof item.weight === 'number' && isFinite(item.weight)) {
                weight = Math.max(0, Math.min(1, item.weight));
            } else {
                weight = maxMag > 0 ? (magnitudes[i] ?? 0) / maxMag : 0;
            }

            const row = document.createElement('pk-report-top-row');
            row.setAttribute('label', item.label ?? '');
            row.setAttribute('value', String(item.value ?? ''));
            row.setAttribute('weight', weight.toFixed(4));
            row.setAttribute('data-dimension-value',
                item.dimensionValue ?? item.dimension ?? item.label ?? '');
            if (item.danger) row.setAttribute('data-danger', 'true');
            this.appendChild(row);
        });
        // slotchange re-applies max-rows visibility.
    }

    private _upgradeProperty(prop: string): void {
        if (Object.prototype.hasOwnProperty.call(this, prop)) {
            const v: unknown = Reflect.get(this, prop);
            Reflect.deleteProperty(this, prop);
            Reflect.set(this, prop, v);
        }
    }

    disconnectedCallback(): void {
        const slot = this._root.querySelector('slot:not([name])');
        slot?.removeEventListener('slotchange', this._slotChangeHandler);
    }

    attributeChangedCallback(): void {
        this._doRender();
    }

    private _maxRows(): number {
        const v = parseInt(this.getAttribute('max-rows') ?? '10', 10);
        return isNaN(v) || v <= 0 ? 10 : v;
    }

    private _onSlotChange(): void {
        // Apply max-rows visibility to slotted pk-report-top-row children
        const rows = Array.from(this.querySelectorAll('pk-report-top-row'));
        const max  = this._maxRows();
        rows.forEach((row, i) => {
            (row as HTMLElement).style.display = i < max ? '' : 'none';
        });
    }

    // pk-row-action event bubbled from child rows — re-dispatch with
    // composed: true so consumer can listen on <pk-report-top-list> itself.
    // (Children fire it on themselves; this element re-emits from its level.)
    private _template(): TemplateResult {
        const title   = this.getAttribute('title');
        const hasRows = this.querySelector('pk-report-top-row') !== null;

        return html`
            <style>${sharedTokens}${listStyles}</style>
            <div class="top-list">
                ${title ? html`
                    <div class="top-list-header">
                        <p class="top-list-title">${title}</p>
                    </div>
                ` : nothing}
                <div class="top-list-body">
                    <slot @slotchange=${() => this._onSlotChange()}></slot>
                    ${!hasRows ? html`
                        <div class="empty-state-wrapper">
                            <slot name="empty-state"></slot>
                        </div>
                    ` : nothing}
                </div>
            </div>
        `;
    }

    private _doRender(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkReportTopList.tagName)) {
    customElements.define(PkReportTopList.tagName, PkReportTopList);
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * `<pk-report-top-row>` — a single ranked row within `<pk-report-top-list>`. v0.7
 *
 * Attributes:
 *   `label`               — dimension label (required).
 *   `value`               — display value string (required).
 *   `weight`              — 0..1 proportion; bar width = weight × 100% (required).
 *   `data-dimension-value`— opaque string passed back in `pk-row-action.detail.dimensionValue`.
 *                           Defaults to the label if omitted.
 *
 * Events (fired on self, composed + bubbling):
 *   `pk-row-action` — `detail: { dimensionValue: string, label: string, value: string }`.
 */
export class PkReportTopRow extends HTMLElement {
    static readonly tagName = 'pk-report-top-row';

    static get observedAttributes(): string[] {
        return ['label', 'value', 'weight', 'data-dimension-value'];
    }

    private readonly _root: ShadowRoot;

    constructor() {
        super();
        this._root = this.attachShadow({ mode: 'open' });
    }

    connectedCallback(): void {
        this._doRender();
    }

    attributeChangedCallback(): void {
        this._doRender();
    }

    private _weight(): number {
        const v = parseFloat(this.getAttribute('weight') ?? '0');
        if (isNaN(v)) return 0;
        return Math.max(0, Math.min(1, v));
    }

    private _handleClick(): void {
        const label          = this.getAttribute('label') ?? '';
        const value          = this.getAttribute('value') ?? '';
        const dimensionValue = this.getAttribute('data-dimension-value') ?? label;

        this.dispatchEvent(new CustomEvent('pk-row-action', {
            bubbles:  true,
            composed: true,
            detail:   { dimensionValue, label, value },
        }));
    }

    private _template(): TemplateResult {
        const label  = this.getAttribute('label') ?? '';
        const value  = this.getAttribute('value') ?? '';
        const weight = this._weight();
        const barPct = `${(weight * 100).toFixed(1)}%`;

        return html`
            <style>
                ${sharedTokens}
                :host {
                    display: block;
                    font-family: var(--pk-font-family, 'Segoe UI', sans-serif);
                }
                .top-row {
                    display: grid;
                    grid-template-columns: minmax(100px, 1fr) auto;
                    grid-template-rows: auto auto;
                    gap: 0 var(--pk-space-3, 0.6rem);
                    align-items: center;
                    padding: var(--pk-space-2, 0.4rem) var(--pk-space-5, 1rem);
                    cursor: pointer;
                    transition: background var(--pk-motion-fast, 0.08s ease);
                }
                .top-row:hover {
                    background: var(--pk-color-bg-sunken, #FBFAF5);
                }
                .top-row:focus-visible {
                    outline: none;
                    box-shadow: inset 0 0 0 2px var(--pk-color-primary, #0e7c7b);
                }
                .row-label {
                    grid-column: 1;
                    grid-row: 1;
                    font-size: var(--pk-font-size-sm, 0.85rem);
                    color: var(--pk-color-text, #181B22);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .row-value {
                    grid-column: 2;
                    grid-row: 1;
                    font-size: var(--pk-font-size-sm, 0.85rem);
                    font-weight: var(--pk-font-weight-bold, 700);
                    color: var(--pk-color-text, #181B22);
                    text-align: right;
                    white-space: nowrap;
                }
                .row-bar-track {
                    grid-column: 1 / -1;
                    grid-row: 2;
                    height: 4px;
                    background: var(--pk-report-bar-bg, #FBFAF5);
                    border-radius: 2px;
                    margin-top: var(--pk-space-1, 0.2rem);
                    overflow: hidden;
                }
                .row-bar-fill {
                    height: 100%;
                    background: var(--pk-report-bar-fill, #0e7c7b);
                    border-radius: 2px;
                    transition: width 0.3s ease;
                }
            </style>
            <div class="top-row"
                 role="button"
                 tabindex="0"
                 @click=${() => this._handleClick()}
                 @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this._handleClick(); } }}>
                <span class="row-label">${label}</span>
                <span class="row-value">${value}</span>
                <div class="row-bar-track">
                    <div class="row-bar-fill" style="width: ${barPct}"></div>
                </div>
            </div>
        `;
    }

    private _doRender(): void {
        render(this._template(), this._root);
    }
}

if (!customElements.get(PkReportTopRow.tagName)) {
    customElements.define(PkReportTopRow.tagName, PkReportTopRow);
}

declare global {
    interface HTMLElementTagNameMap {
        'pk-report-top-list': PkReportTopList;
        'pk-report-top-row':  PkReportTopRow;
    }
}
